/**
 * Mailing settings — interval, per-chat delay, send mode, active template,
 * single account, quiet hours, smart pause, spintax.
 *
 * Bound to the live MailingSettings query; PATCHes on Save.
 */

import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  Clock,
  Eye,
  Gauge,
  Heart,
  Keyboard,
  MessageSquare as MessageIcon,
  Moon,
  MousePointer2,
  RotateCcw,
  Shuffle,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Timer,
  Trash2,
  User as UserIcon,
  Users,
  Wand2,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button, Input, Sheet } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import { useAccounts } from "@/api/accounts";
import { useTemplates } from "@/api/templates";
import {
  useMailingSettings,
  useUpdateMailingSettings,
  type MailingSettings,
  type SafetyPreset,
  type SendMode,
} from "@/api/mailing";

interface Props {
  open: boolean;
  onClose: () => void;
}

const OPTIMAL_REACTION_SETTINGS = {
  minInterval: 45,
  maxInterval: 120,
  dailyPerAccount: 12,
  dailyPerChat: 6,
  authorCooldown: 24,
  skipChance: 15,
  emojis: ["👍", "🔥", "👏", "❤", "😁"],
};

const OPTIMAL_REACTION_EMOJIS_TEXT = OPTIMAL_REACTION_SETTINGS.emojis.join(" ");

type SafetyPresetOption = Exclude<SafetyPreset, "custom">;

interface SafetyPresetConfig {
  label: string;
  status: string;
  tone: string;
  delayMultiplier: number;
  typingMinCpm: number;
  typingMaxCpm: number;
  profileViewChance: number;
  scrollChance: number;
  typoChance: number;
  deleteChance: number;
  sleepEnabled: boolean;
  sleepStart: number;
  sleepEnd: number;
  dailyActionLimit: number;
  floodCooldownMultiplier: number;
  actionLockEnabled: boolean;
}

const SAFETY_PRESETS: Record<SafetyPresetOption, SafetyPresetConfig> = {
  off: {
    label: "Выкл",
    status: "Отключено",
    tone: "text-text-muted",
    delayMultiplier: 1,
    typingMinCpm: 100,
    typingMaxCpm: 150,
    profileViewChance: 0,
    scrollChance: 0,
    typoChance: 0,
    deleteChance: 0,
    sleepEnabled: false,
    sleepStart: 60,
    sleepEnd: 420,
    dailyActionLimit: 0,
    floodCooldownMultiplier: 1,
    actionLockEnabled: false,
  },
  conservative: {
    label: "Консерв.",
    status: "Консервативный",
    tone: "text-emerald-300",
    delayMultiplier: 1.5,
    typingMinCpm: 40,
    typingMaxCpm: 60,
    profileViewChance: 90,
    scrollChance: 50,
    typoChance: 8,
    deleteChance: 3,
    sleepEnabled: true,
    sleepStart: 60,
    sleepEnd: 420,
    dailyActionLimit: 30,
    floodCooldownMultiplier: 1.5,
    actionLockEnabled: true,
  },
  balanced: {
    label: "Баланс",
    status: "Сбалансированный",
    tone: "text-sky-300",
    delayMultiplier: 1,
    typingMinCpm: 100,
    typingMaxCpm: 150,
    profileViewChance: 70,
    scrollChance: 30,
    typoChance: 5,
    deleteChance: 2,
    sleepEnabled: false,
    sleepStart: 60,
    sleepEnd: 420,
    dailyActionLimit: 60,
    floodCooldownMultiplier: 1.25,
    actionLockEnabled: true,
  },
  aggressive: {
    label: "Агрессив.",
    status: "Агрессивный",
    tone: "text-amber-300",
    delayMultiplier: 0.75,
    typingMinCpm: 150,
    typingMaxCpm: 220,
    profileViewChance: 35,
    scrollChance: 15,
    typoChance: 2,
    deleteChance: 1,
    sleepEnabled: false,
    sleepStart: 60,
    sleepEnd: 420,
    dailyActionLimit: 100,
    floodCooldownMultiplier: 1,
    actionLockEnabled: true,
  },
};

export function MailingSettingsSheet({ open, onClose }: Props) {
  const { data: settings } = useMailingSettings();
  const { data: templates } = useTemplates();
  const { data: accounts } = useAccounts();
  const updateMut = useUpdateMailingSettings();

  const [intervalMin, setIntervalMin] = useState<number>(15);
  const [perChatDelay, setPerChatDelay] = useState<number>(8);
  const [sendMode, setSendMode] = useState<SendMode>("round_robin");
  const [singleAccountId, setSingleAccountId] = useState<number | null>(null);
  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);
  const [hiddenMentions, setHiddenMentions] = useState(false);
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState(23 * 60);
  const [quietEnd, setQuietEnd] = useState(8 * 60);
  const [smartPause, setSmartPause] = useState(true);
  const [spintax, setSpintax] = useState(false);
  const [humanSim, setHumanSim] = useState(true);
  const [warmup, setWarmup] = useState(true);
  const [safetyPreset, setSafetyPreset] = useState<SafetyPreset>("balanced");
  const [safetyDelayMultiplier, setSafetyDelayMultiplier] = useState(1);
  const [safetyTypingMinCpm, setSafetyTypingMinCpm] = useState(100);
  const [safetyTypingMaxCpm, setSafetyTypingMaxCpm] = useState(150);
  const [safetyProfileViewChance, setSafetyProfileViewChance] = useState(70);
  const [safetyScrollChance, setSafetyScrollChance] = useState(30);
  const [safetyTypoChance, setSafetyTypoChance] = useState(5);
  const [safetyDeleteChance, setSafetyDeleteChance] = useState(2);
  const [safetySleepEnabled, setSafetySleepEnabled] = useState(false);
  const [safetySleepStart, setSafetySleepStart] = useState(60);
  const [safetySleepEnd, setSafetySleepEnd] = useState(420);
  const [safetyDailyActionLimit, setSafetyDailyActionLimit] = useState(60);
  const [safetyFloodCooldownMultiplier, setSafetyFloodCooldownMultiplier] = useState(1.25);
  const [safetyActionLockEnabled, setSafetyActionLockEnabled] = useState(true);
  const [safetyAdvancedOpen, setSafetyAdvancedOpen] = useState(false);
  const [reactionsEnabled, setReactionsEnabled] = useState(false);
  const [reactionMinInterval, setReactionMinInterval] = useState(45);
  const [reactionMaxInterval, setReactionMaxInterval] = useState(120);
  const [reactionDailyPerAccount, setReactionDailyPerAccount] = useState(12);
  const [reactionDailyPerChat, setReactionDailyPerChat] = useState(6);
  const [reactionAuthorCooldown, setReactionAuthorCooldown] = useState(24);
  const [reactionSkipChance, setReactionSkipChance] = useState(15);
  const [reactionEmojisText, setReactionEmojisText] = useState("👍 🔥 👏 ❤ 😁");
  const [reactionAdvancedOpen, setReactionAdvancedOpen] = useState(false);

  // Seed when sheet opens
  useEffect(() => {
    if (!open || !settings) return;
    setIntervalMin(settings.mail_interval_minutes);
    setPerChatDelay(settings.per_chat_delay_seconds);
    setSendMode(settings.send_mode);
    setSingleAccountId(settings.single_account_id);
    setActiveTemplateId(settings.active_mail_template_id);
    setHiddenMentions(settings.hidden_mentions_enabled);
    setQuietEnabled(settings.quiet_mode_enabled);
    setQuietStart(settings.quiet_start_msk_minute);
    setQuietEnd(settings.quiet_end_msk_minute);
    setSmartPause(settings.smart_pause_enabled);
    setSpintax(settings.spintax_enabled);
    setHumanSim(settings.human_simulation_enabled);
    setWarmup(settings.warmup_enabled);
    setSafetyPreset(settings.safety_preset);
    setSafetyDelayMultiplier(settings.safety_delay_multiplier);
    setSafetyTypingMinCpm(settings.safety_typing_min_cpm);
    setSafetyTypingMaxCpm(settings.safety_typing_max_cpm);
    setSafetyProfileViewChance(settings.safety_profile_view_chance_percent);
    setSafetyScrollChance(settings.safety_scroll_chance_percent);
    setSafetyTypoChance(settings.safety_typo_chance_percent);
    setSafetyDeleteChance(settings.safety_delete_chance_percent);
    setSafetySleepEnabled(settings.safety_sleep_enabled);
    setSafetySleepStart(settings.safety_sleep_start_msk_minute);
    setSafetySleepEnd(settings.safety_sleep_end_msk_minute);
    setSafetyDailyActionLimit(settings.safety_daily_action_limit_per_account);
    setSafetyFloodCooldownMultiplier(settings.safety_flood_cooldown_multiplier);
    setSafetyActionLockEnabled(settings.safety_action_lock_enabled);
    setSafetyAdvancedOpen(settings.safety_preset === "custom");
    setReactionsEnabled(settings.reactions_enabled);
    const loadedEmojis =
      settings.reaction_emojis.length > 0
        ? settings.reaction_emojis
        : OPTIMAL_REACTION_SETTINGS.emojis;
    setReactionMinInterval(settings.reaction_min_interval_minutes);
    setReactionMaxInterval(settings.reaction_max_interval_minutes);
    setReactionDailyPerAccount(settings.reaction_daily_limit_per_account);
    setReactionDailyPerChat(settings.reaction_daily_limit_per_chat);
    setReactionAuthorCooldown(settings.reaction_author_cooldown_hours);
    setReactionSkipChance(settings.reaction_skip_chance_percent);
    setReactionEmojisText(loadedEmojis.join(" "));
    setReactionAdvancedOpen(
      !isOptimalReactionSettings({
        reactionMinInterval: settings.reaction_min_interval_minutes,
        reactionMaxInterval: settings.reaction_max_interval_minutes,
        reactionDailyPerAccount: settings.reaction_daily_limit_per_account,
        reactionDailyPerChat: settings.reaction_daily_limit_per_chat,
        reactionAuthorCooldown: settings.reaction_author_cooldown_hours,
        reactionSkipChance: settings.reaction_skip_chance_percent,
        reactionEmojis: loadedEmojis,
      }),
    );
  }, [open, settings]);

  if (!settings) return null;

  const reactionEmojis = parseReactionEmojis(reactionEmojisText);
  const reactionSettingsAreOptimal = isOptimalReactionSettings({
    reactionMinInterval,
    reactionMaxInterval,
    reactionDailyPerAccount,
    reactionDailyPerChat,
    reactionAuthorCooldown,
    reactionSkipChance,
    reactionEmojis,
  });

  const dirty = isDirty(settings, {
    interval: intervalMin,
    perChatDelay,
    sendMode,
    singleAccountId,
    activeTemplateId,
    hiddenMentions,
    quietEnabled,
    quietStart,
    quietEnd,
    smartPause,
    spintax,
    humanSim,
    warmup,
    safetyPreset,
    safetyDelayMultiplier,
    safetyTypingMinCpm,
    safetyTypingMaxCpm,
    safetyProfileViewChance,
    safetyScrollChance,
    safetyTypoChance,
    safetyDeleteChance,
    safetySleepEnabled,
    safetySleepStart,
    safetySleepEnd,
    safetyDailyActionLimit,
    safetyFloodCooldownMultiplier,
    safetyActionLockEnabled,
    reactionsEnabled,
    reactionMinInterval,
    reactionMaxInterval,
    reactionDailyPerAccount,
    reactionDailyPerChat,
    reactionAuthorCooldown,
    reactionSkipChance,
    reactionEmojis,
  });

  const activeSafetyPreset =
    safetyPreset === "custom" ? null : SAFETY_PRESETS[safetyPreset as SafetyPresetOption];
  const safetyStatus = activeSafetyPreset?.status ?? "Ручная настройка";
  const safetyTone = activeSafetyPreset?.tone ?? "text-violet-300";
  const safetySummary = buildSafetySummary({
    delayMultiplier: safetyDelayMultiplier,
    typingMinCpm: safetyTypingMinCpm,
    typingMaxCpm: safetyTypingMaxCpm,
    profileViewChance: safetyProfileViewChance,
    scrollChance: safetyScrollChance,
    typoChance: safetyTypoChance,
    deleteChance: safetyDeleteChance,
    sleepEnabled: safetySleepEnabled,
    sleepStart: safetySleepStart,
    sleepEnd: safetySleepEnd,
  });

  const applySafetyPreset = (preset: SafetyPresetOption) => {
    const next = SAFETY_PRESETS[preset];
    setSafetyPreset(preset);
    setSafetyDelayMultiplier(next.delayMultiplier);
    setSafetyTypingMinCpm(next.typingMinCpm);
    setSafetyTypingMaxCpm(next.typingMaxCpm);
    setSafetyProfileViewChance(next.profileViewChance);
    setSafetyScrollChance(next.scrollChance);
    setSafetyTypoChance(next.typoChance);
    setSafetyDeleteChance(next.deleteChance);
    setSafetySleepEnabled(next.sleepEnabled);
    setSafetySleepStart(next.sleepStart);
    setSafetySleepEnd(next.sleepEnd);
    setSafetyDailyActionLimit(next.dailyActionLimit);
    setSafetyFloodCooldownMultiplier(next.floodCooldownMultiplier);
    setSafetyActionLockEnabled(next.actionLockEnabled);
    haptic.select();
  };

  const markSafetyCustom = () => {
    if (safetyPreset !== "custom") {
      setSafetyPreset("custom");
    }
  };

  const applyOptimalReactionSettings = () => {
    setReactionMinInterval(OPTIMAL_REACTION_SETTINGS.minInterval);
    setReactionMaxInterval(OPTIMAL_REACTION_SETTINGS.maxInterval);
    setReactionDailyPerAccount(OPTIMAL_REACTION_SETTINGS.dailyPerAccount);
    setReactionDailyPerChat(OPTIMAL_REACTION_SETTINGS.dailyPerChat);
    setReactionAuthorCooldown(OPTIMAL_REACTION_SETTINGS.authorCooldown);
    setReactionSkipChance(OPTIMAL_REACTION_SETTINGS.skipChance);
    setReactionEmojisText(OPTIMAL_REACTION_EMOJIS_TEXT);
  };

  const handleReactionsToggle = (next: boolean) => {
    setReactionsEnabled(next);
    if (next && reactionEmojis.length === 0) {
      applyOptimalReactionSettings();
    }
    if (!next) {
      setReactionAdvancedOpen(false);
    }
  };

  const handleSave = async () => {
    haptic.impact("medium");
    if (reactionEmojis.length === 0) {
      haptic.notify("error");
      toast.error("Добавьте хотя бы одну реакцию");
      return;
    }
    if (reactionMinInterval > reactionMaxInterval) {
      haptic.notify("error");
      toast.error("Минимальный интервал реакций не может быть больше максимального");
      return;
    }
    if (safetyTypingMinCpm > safetyTypingMaxCpm) {
      haptic.notify("error");
      toast.error("Минимальная скорость набора не может быть выше максимальной");
      return;
    }
    try {
      await updateMut.mutateAsync({
        mail_interval_minutes: intervalMin,
        per_chat_delay_seconds: perChatDelay,
        send_mode: sendMode,
        single_account_id: sendMode === "single" ? singleAccountId ?? 0 : 0,
        active_mail_template_id: activeTemplateId ?? 0,
        hidden_mentions_enabled: hiddenMentions,
        quiet_mode_enabled: quietEnabled,
        quiet_start_msk_minute: quietStart,
        quiet_end_msk_minute: quietEnd,
        smart_pause_enabled: smartPause,
        spintax_enabled: spintax,
        human_simulation_enabled: humanSim,
        warmup_enabled: warmup,
        safety_preset: safetyPreset,
        safety_delay_multiplier: safetyDelayMultiplier,
        safety_typing_min_cpm: safetyTypingMinCpm,
        safety_typing_max_cpm: safetyTypingMaxCpm,
        safety_profile_view_chance_percent: safetyProfileViewChance,
        safety_scroll_chance_percent: safetyScrollChance,
        safety_typo_chance_percent: safetyTypoChance,
        safety_delete_chance_percent: safetyDeleteChance,
        safety_sleep_enabled: safetySleepEnabled,
        safety_sleep_start_msk_minute: safetySleepStart,
        safety_sleep_end_msk_minute: safetySleepEnd,
        safety_daily_action_limit_per_account: safetyDailyActionLimit,
        safety_flood_cooldown_multiplier: safetyFloodCooldownMultiplier,
        safety_action_lock_enabled: safetyActionLockEnabled,
        reactions_enabled: reactionsEnabled,
        reaction_min_interval_minutes: reactionMinInterval,
        reaction_max_interval_minutes: reactionMaxInterval,
        reaction_daily_limit_per_account: reactionDailyPerAccount,
        reaction_daily_limit_per_chat: reactionDailyPerChat,
        reaction_author_cooldown_hours: reactionAuthorCooldown,
        reaction_skip_chance_percent: reactionSkipChance,
        reaction_emojis: reactionEmojis,
      });
      haptic.notify("success");
      toast.success("Настройки сохранены");
      onClose();
    } catch (e) {
      haptic.notify("error");
      toast.error((e as Error)?.message || "Не удалось сохранить");
    }
  };

  const mailTemplates = (templates ?? []).filter((t) => t.kind === "mail");
  const enabledAccounts = (accounts ?? []).filter((a) => a.is_enabled && !a.is_banned);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      size="full"
      title="Настройки рассылки"
      description="Скорость, режим, тишина и умная пауза"
    >
      <div className="space-y-5">
        {/* Safety presets */}
        <Section title="Защита аккаунтов" icon={<ShieldCheck className="size-3.5" />}>
          <div className="glass rounded-2xl p-3.5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
                <ShieldCheck className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold">Анти-фрод режим</div>
                  <span className={cn("text-xs font-semibold", safetyTone)}>
                    {safetyStatus}
                  </span>
                </div>
                <div className="mt-1 text-xs text-text-muted leading-relaxed">
                  {safetySummary}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(Object.keys(SAFETY_PRESETS) as SafetyPresetOption[]).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => applySafetyPreset(preset)}
                  className={cn(
                    "h-10 rounded-xl border text-[12px] font-semibold transition-colors",
                    safetyPreset === preset
                      ? "border-white/35 bg-white/[0.12] text-white"
                      : "border-white/[0.08] bg-white/[0.02] text-text-muted hover:bg-white/[0.05]",
                  )}
                >
                  {SAFETY_PRESETS[preset].label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <SafetyMetric icon={<Gauge />} label="Задержки" value={`x${formatMultiplier(safetyDelayMultiplier)}`} />
              <SafetyMetric icon={<Keyboard />} label="Набор" value={`${safetyTypingMinCpm}-${safetyTypingMaxCpm}/мин`} />
              <SafetyMetric icon={<Eye />} label="Просмотр профиля" value={`${safetyProfileViewChance}%`} />
              <SafetyMetric icon={<MousePointer2 />} label="Пауза скролла" value={`${safetyScrollChance}%`} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                leftIcon={<SlidersHorizontal />}
                onClick={() => setSafetyAdvancedOpen((value) => !value)}
              >
                {safetyAdvancedOpen ? "Скрыть" : "Настроить"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                leftIcon={<RotateCcw />}
                onClick={() => applySafetyPreset("balanced")}
              >
                Баланс
              </Button>
            </div>
          </div>

          <AnimatePresence>
            {safetyAdvancedOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-3 pt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      type="number"
                      label="Множитель задержек"
                      value={safetyDelayMultiplier}
                      min={0.25}
                      max={5}
                      step={0.05}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyDelayMultiplier(clampFloat(parseFloat(e.target.value || "0"), 0.25, 5));
                      }}
                      leftIcon={<Gauge />}
                    />
                    <Input
                      type="number"
                      label="Лимит действий/день"
                      value={safetyDailyActionLimit}
                      min={0}
                      max={1000}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyDailyActionLimit(clamp(parseInt(e.target.value || "0"), 0, 1000));
                      }}
                      leftIcon={<UserIcon />}
                      hint="0 — без общего лимита"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      type="number"
                      label="Набор от, сим/мин"
                      value={safetyTypingMinCpm}
                      min={10}
                      max={600}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyTypingMinCpm(clamp(parseInt(e.target.value || "0"), 10, 600));
                      }}
                      leftIcon={<Keyboard />}
                    />
                    <Input
                      type="number"
                      label="Набор до, сим/мин"
                      value={safetyTypingMaxCpm}
                      min={10}
                      max={600}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyTypingMaxCpm(clamp(parseInt(e.target.value || "0"), 10, 600));
                      }}
                      leftIcon={<Keyboard />}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      type="number"
                      label="Просмотр профиля, %"
                      value={safetyProfileViewChance}
                      min={0}
                      max={100}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyProfileViewChance(clamp(parseInt(e.target.value || "0"), 0, 100));
                      }}
                      leftIcon={<Eye />}
                    />
                    <Input
                      type="number"
                      label="Пауза скролла, %"
                      value={safetyScrollChance}
                      min={0}
                      max={100}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyScrollChance(clamp(parseInt(e.target.value || "0"), 0, 100));
                      }}
                      leftIcon={<MousePointer2 />}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      type="number"
                      label="Опечатки, %"
                      value={safetyTypoChance}
                      min={0}
                      max={100}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyTypoChance(clamp(parseInt(e.target.value || "0"), 0, 100));
                      }}
                      leftIcon={<Keyboard />}
                    />
                    <Input
                      type="number"
                      label="Удаление, %"
                      value={safetyDeleteChance}
                      min={0}
                      max={100}
                      onChange={(e) => {
                        markSafetyCustom();
                        setSafetyDeleteChance(clamp(parseInt(e.target.value || "0"), 0, 100));
                      }}
                      leftIcon={<Trash2 />}
                    />
                  </div>

                  <Input
                    type="number"
                    label="Cooldown после FloodWait"
                    value={safetyFloodCooldownMultiplier}
                    min={1}
                    max={5}
                    step={0.05}
                    onChange={(e) => {
                      markSafetyCustom();
                      setSafetyFloodCooldownMultiplier(clampFloat(parseFloat(e.target.value || "0"), 1, 5));
                    }}
                    leftIcon={<Clock />}
                    hint="x1.25 значит ждём на 25% дольше, чем просит Telegram"
                  />

                  <ToggleRow
                    label="Единый lock аккаунта"
                    subtitle="Рассылка и реакции не используют один аккаунт одновременно"
                    active={safetyActionLockEnabled}
                    onChange={(next) => {
                      markSafetyCustom();
                      setSafetyActionLockEnabled(next);
                    }}
                  />
                  <ToggleRow
                    label={safetySleepEnabled ? "Сон аккаунтов включён" : "Сон аккаунтов выключен"}
                    subtitle="В это окно рассылка и реакции стоят на паузе"
                    active={safetySleepEnabled}
                    onChange={(next) => {
                      markSafetyCustom();
                      setSafetySleepEnabled(next);
                    }}
                  />
                  <AnimatePresence>
                    {safetySleepEnabled && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          <TimeField
                            label="Сон с"
                            minutes={safetySleepStart}
                            onChange={(minutes) => {
                              markSafetyCustom();
                              setSafetySleepStart(minutes);
                            }}
                          />
                          <TimeField
                            label="Сон до"
                            minutes={safetySleepEnd}
                            onChange={(minutes) => {
                              markSafetyCustom();
                              setSafetySleepEnd(minutes);
                            }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>

        {/* Timing */}
        <Section title="Темп" icon={<Timer className="size-3.5" />}>
          <Input
            type="number"
            label="Интервал между итерациями (мин)"
            value={intervalMin}
            min={1}
            max={1440}
            onChange={(e) => setIntervalMin(clamp(parseInt(e.target.value || "0"), 1, 1440))}
            leftIcon={<Clock />}
            hint="Каждые N минут — новый полный круг по чатам"
          />
          <Input
            type="number"
            label="Задержка между чатами (сек)"
            value={perChatDelay}
            min={0}
            max={3600}
            onChange={(e) => setPerChatDelay(clamp(parseInt(e.target.value || "0"), 0, 3600))}
            leftIcon={<Zap />}
            hint="Чем больше — тем человечнее. Рекомендуем 5–15 сек"
          />
        </Section>

        {/* Send mode */}
        <Section title="Аккаунты" icon={<Users className="size-3.5" />}>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04]">
            <ModeRow
              active={sendMode === "round_robin"}
              onClick={() => {
                setSendMode("round_robin");
                haptic.select();
              }}
              icon={<Shuffle className="size-4" />}
              title="По кругу"
              subtitle="Каждую публикацию отправляет следующий аккаунт"
            />
            <ModeRow
              active={sendMode === "single"}
              onClick={() => {
                setSendMode("single");
                haptic.select();
              }}
              icon={<UserIcon className="size-4" />}
              title="Один аккаунт"
              subtitle="Только выбранный аккаунт ведёт рассылку"
            />
          </div>

          <AnimatePresence>
            {sendMode === "single" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] mt-3 max-h-[240px] overflow-y-auto">
                  {enabledAccounts.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-text-muted">
                      Нет активных аккаунтов
                    </div>
                  ) : (
                    enabledAccounts.map((a) => {
                      const active = singleAccountId === a.id;
                      return (
                        <button
                          key={a.id}
                          onClick={() => {
                            setSingleAccountId(active ? null : a.id);
                            haptic.select();
                          }}
                          className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors"
                        >
                          <Checkbox active={active} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium truncate">
                              {a.name || a.phone}
                            </div>
                            <div className="text-[11px] text-text-muted truncate">{a.phone}</div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>

        {/* Active template */}
        <Section title="Шаблон по умолчанию" icon={<Sparkles className="size-3.5" />}>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] max-h-[280px] overflow-y-auto">
            {mailTemplates.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-text-muted">
                Нет шаблонов рассылки — создайте на вкладке «Шаблоны»
              </div>
            ) : (
              mailTemplates.map((t) => {
                const active = activeTemplateId === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      setActiveTemplateId(active ? null : t.id);
                      haptic.select();
                    }}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors"
                  >
                    <Checkbox active={active} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{t.name}</div>
                      <div className="text-[11px] text-text-muted truncate">
                        {previewText(t.text)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </Section>

        {/* Quiet hours */}
        <Section title="Тишина по МСК" icon={<Moon className="size-3.5" />}>
          <ToggleRow
            label={quietEnabled ? "Тихий режим включён" : "Тихий режим выключен"}
            subtitle="В заданном окне рассылка не отправляется"
            active={quietEnabled}
            onChange={setQuietEnabled}
          />
          <AnimatePresence>
            {quietEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <TimeField
                    label="Начало"
                    minutes={quietStart}
                    onChange={setQuietStart}
                  />
                  <TimeField
                    label="Конец"
                    minutes={quietEnd}
                    onChange={setQuietEnd}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>

        {/* Reactions */}
        <Section title="Активность реакциями" icon={<Heart className="size-3.5" />}>
          <ToggleRow
            label={reactionsEnabled ? "Реакции включены" : "Реакции выключены"}
            subtitle="Отдельный воркер ставит одну случайную реакцию за цикл по активным чатам"
            active={reactionsEnabled}
            onChange={handleReactionsToggle}
          />
          <AnimatePresence>
            {reactionsEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-3 mt-3">
                  <div className="glass rounded-2xl p-3.5 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="size-10 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
                        <Sparkles className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-semibold">Оптимальный режим</div>
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                              reactionSettingsAreOptimal
                                ? "bg-emerald-500/10 text-emerald-300"
                                : "bg-white/[0.06] text-text-muted",
                            )}
                          >
                            {reactionSettingsAreOptimal ? "Активен" : "Изменён"}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-text-muted leading-relaxed">
                          {OPTIMAL_REACTION_SETTINGS.minInterval}–
                          {OPTIMAL_REACTION_SETTINGS.maxInterval} мин, до{" "}
                          {OPTIMAL_REACTION_SETTINGS.dailyPerAccount} на аккаунт, до{" "}
                          {OPTIMAL_REACTION_SETTINGS.dailyPerChat} на чат. Кулдаун автора{" "}
                          {OPTIMAL_REACTION_SETTINGS.authorCooldown} ч, случайный пропуск{" "}
                          {OPTIMAL_REACTION_SETTINGS.skipChance}%.
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5 text-lg leading-none">
                          {OPTIMAL_REACTION_SETTINGS.emojis.map((emoji) => (
                            <span key={emoji}>{emoji}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        leftIcon={<SlidersHorizontal />}
                        onClick={() => setReactionAdvancedOpen((value) => !value)}
                      >
                        {reactionAdvancedOpen ? "Скрыть" : "Настроить"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        leftIcon={<RotateCcw />}
                        disabled={reactionSettingsAreOptimal}
                        onClick={applyOptimalReactionSettings}
                      >
                        Сбросить
                      </Button>
                    </div>
                  </div>
                  <AnimatePresence>
                    {reactionAdvancedOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3 pt-1">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input
                              type="number"
                              label="Интервал от, мин"
                              value={reactionMinInterval}
                              min={5}
                              max={1440}
                              onChange={(e) =>
                                setReactionMinInterval(
                                  clamp(parseInt(e.target.value || "0"), 5, 1440),
                                )
                              }
                              leftIcon={<Timer />}
                            />
                            <Input
                              type="number"
                              label="Интервал до, мин"
                              value={reactionMaxInterval}
                              min={5}
                              max={1440}
                              onChange={(e) =>
                                setReactionMaxInterval(
                                  clamp(parseInt(e.target.value || "0"), 5, 1440),
                                )
                              }
                              leftIcon={<Timer />}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input
                              type="number"
                              label="Лимит на аккаунт"
                              value={reactionDailyPerAccount}
                              min={0}
                              max={500}
                              onChange={(e) =>
                                setReactionDailyPerAccount(
                                  clamp(parseInt(e.target.value || "0"), 0, 500),
                                )
                              }
                              leftIcon={<UserIcon />}
                            />
                            <Input
                              type="number"
                              label="Лимит на чат"
                              value={reactionDailyPerChat}
                              min={0}
                              max={500}
                              onChange={(e) =>
                                setReactionDailyPerChat(
                                  clamp(parseInt(e.target.value || "0"), 0, 500),
                                )
                              }
                              leftIcon={<MessageIcon />}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input
                              type="number"
                              label="Кулдаун автора, ч"
                              value={reactionAuthorCooldown}
                              min={0}
                              max={720}
                              onChange={(e) =>
                                setReactionAuthorCooldown(
                                  clamp(parseInt(e.target.value || "0"), 0, 720),
                                )
                              }
                              leftIcon={<Clock />}
                            />
                            <Input
                              type="number"
                              label="Пропуск, %"
                              value={reactionSkipChance}
                              min={0}
                              max={95}
                              onChange={(e) =>
                                setReactionSkipChance(
                                  clamp(parseInt(e.target.value || "0"), 0, 95),
                                )
                              }
                              leftIcon={<Shuffle />}
                            />
                          </div>
                          <Input
                            label="Реакции"
                            value={reactionEmojisText}
                            onChange={(e) => setReactionEmojisText(e.target.value)}
                            leftIcon={<Heart />}
                            hint="Через пробел или запятую"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>

        {/* Advanced */}
        <Section title="Продвинутое" icon={<Wand2 className="size-3.5" />}>
          <ToggleRow
            label="🧍 Имитация живого пользователя"
            subtitle="Открывает чат, показывает «печатает…», потом шлёт сообщение"
            active={humanSim}
            onChange={setHumanSim}
          />
          <ToggleRow
            label="🌱 Прогрев новых аккаунтов"
            subtitle="Постепенно наращивает нагрузку для аккаунтов младше 7 дней"
            active={warmup}
            onChange={setWarmup}
          />
          <ToggleRow
            label="Умная пауза"
            subtitle="После 3 подряд неудач — пауза 1 час, после 12 — исключение"
            active={smartPause}
            onChange={setSmartPause}
          />
          <ToggleRow
            label="Спинтакс"
            subtitle="Поддержка конструкций {вариант1|вариант2} в шаблоне"
            active={spintax}
            onChange={setSpintax}
          />
          <ToggleRow
            label="Скрытые упоминания"
            subtitle="Экспериментально: может повысить риск ограничений аккаунта"
            active={hiddenMentions}
            onChange={setHiddenMentions}
          />
        </Section>

        {/* Actions */}
        <div className="flex gap-3 pt-2 sticky bottom-0 pb-2 bg-gradient-to-t from-bg via-bg to-transparent">
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="flex-1"
            disabled={!dirty || updateMut.isPending}
            loading={updateMut.isPending}
            onClick={handleSave}
            leftIcon={<Check />}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

// ============================================================
// Sub-components
// ============================================================

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs text-text-muted uppercase tracking-wider font-medium px-1">
        <span className="text-text-dim">{icon}</span>
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function SafetyMetric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] px-3 py-2 flex items-center gap-2 min-w-0">
      <span className="text-text-dim shrink-0 [&>svg]:size-3.5">{icon}</span>
      <span className="min-w-0">
        <span className="block text-[10px] text-text-muted truncate">{label}</span>
        <span className="block text-[12px] font-semibold truncate">{value}</span>
      </span>
    </div>
  );
}

function ModeRow({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-white/[0.03] transition-colors"
    >
      <div
        className={cn(
          "size-10 rounded-xl grid place-items-center shrink-0",
          active ? "bg-accent-soft text-accent" : "bg-white/[0.04] text-text-dim",
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium truncate">{title}</div>
        <div className="text-[11px] text-text-muted truncate">{subtitle}</div>
      </div>
      <Checkbox active={active} />
    </button>
  );
}

function ToggleRow({
  label,
  subtitle,
  active,
  onChange,
}: {
  label: string;
  subtitle: string;
  active: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      onClick={() => {
        onChange(!active);
        haptic.select();
      }}
      className="w-full glass rounded-2xl p-3.5 flex items-center gap-3 hover:bg-white/[0.05] transition-colors text-left"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-text-muted">{subtitle}</div>
      </div>
      <div
        className={cn(
          "relative w-10 h-6 rounded-full transition-colors shrink-0",
          active ? "bg-accent" : "bg-white/[0.08]",
        )}
      >
        <motion.div
          animate={{ x: active ? 16 : 0 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
          className="absolute top-0.5 left-0.5 size-5 rounded-full bg-white"
        />
      </div>
    </button>
  );
}

function Checkbox({ active }: { active: boolean }) {
  return (
    <div
      className={cn(
        "size-5 rounded-md border grid place-items-center shrink-0 transition-colors",
        active ? "bg-accent border-accent" : "border-white/[0.15] bg-transparent",
      )}
    >
      <AnimatePresence>
        {active && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="size-3 text-white" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TimeField({
  label,
  minutes,
  onChange,
}: {
  label: string;
  minutes: number;
  onChange: (m: number) => void;
}) {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  const value = `${pad(hh)}:${pad(mm)}`;
  return (
    <Input
      type="time"
      label={label}
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        const [h, m] = v.split(":").map((x) => parseInt(x || "0"));
        if (!Number.isNaN(h) && !Number.isNaN(m)) {
          onChange(clamp(h * 60 + m, 0, 1439));
        }
      }}
    />
  );
}

// ============================================================
// Helpers
// ============================================================

function clamp(n: number, min: number, max: number) {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function clampFloat(n: number, min: number, max: number) {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, Math.round(n * 100) / 100));
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatTime(minutes: number): string {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${pad(hh)}:${pad(mm)}`;
}

function formatMultiplier(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2).replace(/0$/, "");
}

function buildSafetySummary(state: {
  delayMultiplier: number;
  typingMinCpm: number;
  typingMaxCpm: number;
  profileViewChance: number;
  scrollChance: number;
  typoChance: number;
  deleteChance: number;
  sleepEnabled: boolean;
  sleepStart: number;
  sleepEnd: number;
}): string {
  const parts = [
    `Задержки x${formatMultiplier(state.delayMultiplier)}`,
    `Набор ${state.typingMinCpm}-${state.typingMaxCpm} сим/мин`,
    `Просмотр профиля ${state.profileViewChance}%`,
    `Скроллинг ${state.scrollChance}%`,
    `Опечатки ${state.typoChance}%`,
    `Удаление ${state.deleteChance}%`,
  ];
  if (state.sleepEnabled) {
    parts.push(`Сон ${formatTime(state.sleepStart)}-${formatTime(state.sleepEnd)}`);
  }
  return parts.join(" · ");
}

function previewText(text: string): string {
  return text
    .replace(/\{([^|}]+)\|[^}]*\}/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
}

function parseReactionEmojis(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\s,;]+/g)
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  ).slice(0, 24);
}

function isOptimalReactionSettings(state: {
  reactionMinInterval: number;
  reactionMaxInterval: number;
  reactionDailyPerAccount: number;
  reactionDailyPerChat: number;
  reactionAuthorCooldown: number;
  reactionSkipChance: number;
  reactionEmojis: string[];
}): boolean {
  return (
    state.reactionMinInterval === OPTIMAL_REACTION_SETTINGS.minInterval &&
    state.reactionMaxInterval === OPTIMAL_REACTION_SETTINGS.maxInterval &&
    state.reactionDailyPerAccount === OPTIMAL_REACTION_SETTINGS.dailyPerAccount &&
    state.reactionDailyPerChat === OPTIMAL_REACTION_SETTINGS.dailyPerChat &&
    state.reactionAuthorCooldown === OPTIMAL_REACTION_SETTINGS.authorCooldown &&
    state.reactionSkipChance === OPTIMAL_REACTION_SETTINGS.skipChance &&
    state.reactionEmojis.join("\n") === OPTIMAL_REACTION_SETTINGS.emojis.join("\n")
  );
}

function isDirty(
  s: MailingSettings,
  state: {
    interval: number;
    perChatDelay: number;
    sendMode: SendMode;
    singleAccountId: number | null;
    activeTemplateId: number | null;
    hiddenMentions: boolean;
    quietEnabled: boolean;
    quietStart: number;
    quietEnd: number;
    smartPause: boolean;
    spintax: boolean;
    humanSim: boolean;
    warmup: boolean;
    safetyPreset: SafetyPreset;
    safetyDelayMultiplier: number;
    safetyTypingMinCpm: number;
    safetyTypingMaxCpm: number;
    safetyProfileViewChance: number;
    safetyScrollChance: number;
    safetyTypoChance: number;
    safetyDeleteChance: number;
    safetySleepEnabled: boolean;
    safetySleepStart: number;
    safetySleepEnd: number;
    safetyDailyActionLimit: number;
    safetyFloodCooldownMultiplier: number;
    safetyActionLockEnabled: boolean;
    reactionsEnabled: boolean;
    reactionMinInterval: number;
    reactionMaxInterval: number;
    reactionDailyPerAccount: number;
    reactionDailyPerChat: number;
    reactionAuthorCooldown: number;
    reactionSkipChance: number;
    reactionEmojis: string[];
  },
): boolean {
  const currentReactions = (s.reaction_emojis ?? []).join("\n");
  const nextReactions = state.reactionEmojis.join("\n");
  return (
    s.mail_interval_minutes !== state.interval ||
    s.per_chat_delay_seconds !== state.perChatDelay ||
    s.send_mode !== state.sendMode ||
    s.single_account_id !== state.singleAccountId ||
    s.active_mail_template_id !== state.activeTemplateId ||
    s.hidden_mentions_enabled !== state.hiddenMentions ||
    s.quiet_mode_enabled !== state.quietEnabled ||
    s.quiet_start_msk_minute !== state.quietStart ||
    s.quiet_end_msk_minute !== state.quietEnd ||
    s.smart_pause_enabled !== state.smartPause ||
    s.spintax_enabled !== state.spintax ||
    s.human_simulation_enabled !== state.humanSim ||
    s.warmup_enabled !== state.warmup ||
    s.safety_preset !== state.safetyPreset ||
    s.safety_delay_multiplier !== state.safetyDelayMultiplier ||
    s.safety_typing_min_cpm !== state.safetyTypingMinCpm ||
    s.safety_typing_max_cpm !== state.safetyTypingMaxCpm ||
    s.safety_profile_view_chance_percent !== state.safetyProfileViewChance ||
    s.safety_scroll_chance_percent !== state.safetyScrollChance ||
    s.safety_typo_chance_percent !== state.safetyTypoChance ||
    s.safety_delete_chance_percent !== state.safetyDeleteChance ||
    s.safety_sleep_enabled !== state.safetySleepEnabled ||
    s.safety_sleep_start_msk_minute !== state.safetySleepStart ||
    s.safety_sleep_end_msk_minute !== state.safetySleepEnd ||
    s.safety_daily_action_limit_per_account !== state.safetyDailyActionLimit ||
    s.safety_flood_cooldown_multiplier !== state.safetyFloodCooldownMultiplier ||
    s.safety_action_lock_enabled !== state.safetyActionLockEnabled ||
    s.reactions_enabled !== state.reactionsEnabled ||
    s.reaction_min_interval_minutes !== state.reactionMinInterval ||
    s.reaction_max_interval_minutes !== state.reactionMaxInterval ||
    s.reaction_daily_limit_per_account !== state.reactionDailyPerAccount ||
    s.reaction_daily_limit_per_chat !== state.reactionDailyPerChat ||
    s.reaction_author_cooldown_hours !== state.reactionAuthorCooldown ||
    s.reaction_skip_chance_percent !== state.reactionSkipChance ||
    currentReactions !== nextReactions
  );
}
